import type { JSX } from 'react';

import * as styles from './DataTableGanttAnnotationsIndicators.sty.js';
import { DATA_TABLE_ROW_BORDER } from '../../constants.js';
import { useScaleAndFormatter } from './hooks/useScaleAndFormatter.js';
import { toIntegrationTracks } from './to-integration-tracks.js';
import { AreaIndicator } from '../../../../charts/core/components/annotations/components/indicators/AreaIndicator.js';
import { LineIndicator } from '../../../../charts/core/components/annotations/components/indicators/LineIndicator.js';
import type { IndicatorData } from '../../../../charts/core/components/annotations/components/indicators/types.js';
import { getShownIndicators } from '../../../../charts/core/components/annotations/components/indicators/utils/get-shown-indicators.js';
import { getUniqueIndicatorsByPriority } from '../../../../charts/core/components/annotations/components/indicators/utils/get-unique-indicators-by-priority.js';
import { getMarkerBrightnessBasedOnState } from '../../../../charts/core/components/annotations/utils/get-marker-brightness-based-on-state.js';
import { isRangeMarker } from '../../../../charts/core/components/annotations/utils/is-range-marker.js';
import { serializeMarkerRange } from '../../../../charts/core/components/annotations/utils/serialize-marker-range.js';
import { BrightnessFilter } from '../../../../charts/core/components/BrightnessFilter.js';
import { useCurrentTheme } from '../../../../core/hooks/useCurrentTheme.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import type { DataTableGanttColumnConfig } from '../ColumnTypes/column-types-types.js';

type DataTableGanttAnnotationIndicatorsProps = Readonly<{
  width: number;
  height: number;
  min: number;
  max: number;
  config: DataTableGanttColumnConfig;
  columnId: string;
  isLastPageRow: boolean;
}>;

function getBrightnessFilterId(
  indicator: IndicatorData,
  trackIndex: number,
): string {
  const { start, end } = indicator;
  return `gantt_brightness_${serializeMarkerRange(
    {
      start: start.valueOf(),
      end: end ? end.valueOf() : undefined,
    },
    trackIndex,
  )}`;
}

function useGanttIndicators(
  config: DataTableGanttColumnConfig,
  columnId: string,
  range: [number, number],
): [string, IndicatorData][][] | null {
  const table = useDataTable();
  const { tracks: configuredTracks } = config.annotationsHeader ?? {};

  if (!configuredTracks?.length) {
    return null;
  }

  const selection =
    table.getState().ganttAnnotationsSelection?.[columnId] ?? {};
  const tracks = toIntegrationTracks(configuredTracks);
  const shownIndicators = getShownIndicators(tracks, selection);
  const uniqueIndicators = getUniqueIndicatorsByPriority(
    shownIndicators,
    range,
  );

  const hasIndicators = uniqueIndicators.some((track) => track.length > 0);
  return hasIndicators ? uniqueIndicators : null;
}

function GanttIndicatorShape({
  indicator,
  trackIndex,
  scale,
  height,
  theme,
}: Readonly<{
  indicator: IndicatorData;
  trackIndex: number;
  scale: (value: number | Date) => number;
  height: number;
  theme: 'light' | 'dark';
}>): JSX.Element {
  const { start, end, color, state } = indicator;
  const brightnessFilterId = getBrightnessFilterId(indicator, trackIndex);
  const slope = getMarkerBrightnessBasedOnState(theme, state);
  const filter = `url(#${brightnessFilterId})`;
  const range = { start, end };
  const x = scale(start) ?? 0;

  return (
    <>
      <BrightnessFilter id={brightnessFilterId} slope={slope} />
      {isRangeMarker(range) ? (
        <AreaIndicator
          x={x}
          width={(scale(range.end) ?? 0) - x}
          height={height}
          color={color}
          filter={filter}
        />
      ) : (
        <LineIndicator x={x} height={height} color={color} filter={filter} />
      )}
    </>
  );
}

/**
 * Renders indicators for gantt chart annotations that correspond to the annotation marker positions in the header.
 *
 * Indicators are rendered as SVG elements on top of the gantt chart cells, and their visibility is determined based on
 * the current selection state and the configuration provided in the annotations header.
 *
 * @internal
 */
export const DataTableGanttAnnotationIndicators = ({
  width,
  height,
  min,
  max,
  config,
  columnId,
  isLastPageRow,
}: DataTableGanttAnnotationIndicatorsProps): JSX.Element | null => {
  const theme = useCurrentTheme();
  const { scale } = useScaleAndFormatter(width, config, min, max);
  const indicators = useGanttIndicators(config, columnId, [min, max]);

  if (!indicators) {
    return null;
  }

  // Extend the indicators' height to stretch over the row borders so they appear continuous, across rows.
  // Since the last body row has no bottom border, don't extend the height for it to avoid unnecessary overflow.
  const fullRowHeight = height + (isLastPageRow ? 0 : DATA_TABLE_ROW_BORDER);

  return (
    <svg
      height={fullRowHeight}
      width={width}
      className={styles.annotationIndicators}
    >
      {indicators.map((track, trackIndex) =>
        track.map(([key, indicator]) => (
          <GanttIndicatorShape
            key={key}
            indicator={indicator}
            trackIndex={trackIndex}
            scale={scale}
            height={fullRowHeight}
            theme={theme}
          />
        )),
      )}
    </svg>
  );
};
